import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook for managing opportunity filters with URL persistence
 * Handles search term, company stages, role areas, and experience levels
 */
const useOpportunityFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(() => {
    return searchParams.get('search') || '';
  });

  const [filters, setFilters] = useState(() => {
    const companyStages = searchParams.get('stages')?.split(',').filter(Boolean) || [];
    const roleAreas = searchParams.get('roles')?.split(',').filter(Boolean) || [];
    const experienceLevels = searchParams.get('experience')?.split(',').filter(Boolean) || [];
    
    return {
      companyStages,
      roleAreas,
      experienceLevels
    };
  });

  // Update URL when filters change
  const updateURL = useCallback((newSearchTerm, newFilters) => {
    const params = new URLSearchParams();
    
    // Add search term if present
    if (newSearchTerm && newSearchTerm.trim()) {
      params.set('search', newSearchTerm.trim());
    }
    
    // Add company stages if present
    if (newFilters.companyStages && newFilters.companyStages.length > 0) {
      params.set('stages', newFilters.companyStages.join(','));
    }
    
    // Add role areas if present
    if (newFilters.roleAreas && newFilters.roleAreas.length > 0) {
      params.set('roles', newFilters.roleAreas.join(','));
    }
    
    // Add experience levels if present
    if (newFilters.experienceLevels && newFilters.experienceLevels.length > 0) {
      params.set('experience', newFilters.experienceLevels.join(','));
    }
    
    // Update URL without causing navigation
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Handle search term changes
  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    updateURL(newSearchTerm, filters);
  }, [filters, updateURL]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    updateURL(searchTerm, newFilters);
  }, [searchTerm, updateURL]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      companyStages: [],
      roleAreas: [],
      experienceLevels: []
    };
    setSearchTerm('');
    setFilters(clearedFilters);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Check if any filters are active
  const hasActiveFilters = searchTerm.trim() !== '' || 
    filters.companyStages.length > 0 || 
    filters.roleAreas.length > 0 || 
    filters.experienceLevels.length > 0;

  // Get filter summary for display
  const getFilterSummary = useCallback(() => {
    const summary = [];
    
    if (searchTerm.trim()) {
      summary.push(`Search: "${searchTerm.trim()}"`);
    }
    
    if (filters.companyStages.length > 0) {
      summary.push(`${filters.companyStages.length} stage${filters.companyStages.length > 1 ? 's' : ''}`);
    }
    
    if (filters.roleAreas.length > 0) {
      summary.push(`${filters.roleAreas.length} role area${filters.roleAreas.length > 1 ? 's' : ''}`);
    }
    
    if (filters.experienceLevels.length > 0) {
      summary.push(`${filters.experienceLevels.length} experience level${filters.experienceLevels.length > 1 ? 's' : ''}`);
    }
    
    return summary.join(', ');
  }, [searchTerm, filters]);

  // Sync with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlSearchTerm = searchParams.get('search') || '';
    const urlCompanyStages = searchParams.get('stages')?.split(',').filter(Boolean) || [];
    const urlRoleAreas = searchParams.get('roles')?.split(',').filter(Boolean) || [];
    const urlExperienceLevels = searchParams.get('experience')?.split(',').filter(Boolean) || [];
    
    setSearchTerm(urlSearchTerm);
    setFilters({
      companyStages: urlCompanyStages,
      roleAreas: urlRoleAreas,
      experienceLevels: urlExperienceLevels
    });
  }, [searchParams]);

  return {
    searchTerm,
    filters,
    handleSearchChange,
    handleFilterChange,
    clearAllFilters,
    hasActiveFilters,
    getFilterSummary
  };
};

export default useOpportunityFilters;